import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/marketing/lib/utils";
import { Badge } from "@/marketing/components/ui/badge";
import {
  type InfluencerCampaign,
  InfluencerCampaignStatus,
  STATUS_META,
  STATUS_ORDER,
} from "@/marketing/models/influencerCampaign";

import { CampaignCard } from "./CampaignCard";
import { moveCampaignInOrder } from "./campaignOrder";
import { useDragAutoScroll } from "./useDragAutoScroll";

interface CampaignBoardViewProps {
  grouped: Record<InfluencerCampaignStatus, InfluencerCampaign[]>;
  completedCollapsed: boolean;
  isReorderEnabled: boolean;
  onToggleCompleted: () => void;
  onCardClick: (campaign: InfluencerCampaign) => void;
  onCardEdit: (campaign: InfluencerCampaign) => void;
  onCardDrop: (campaignId: string, status: InfluencerCampaignStatus) => void;
  onReorder: (orderedCampaignIds: string[]) => void;
  onReorderCommit: () => void;
}

export function CampaignBoardView({
  grouped,
  completedCollapsed,
  isReorderEnabled,
  onToggleCompleted,
  onCardClick,
  onCardEdit,
  onCardDrop,
  onReorder,
  onReorderCommit,
}: CampaignBoardViewProps) {
  // Sorting is an explicit mode, not something a drag falls into: entering it on dragstart replaced
  // the card mid-drag, which cancelled the browser drag and broke moving cards between statuses.
  // The whole session is saved once on exit rather than per move.
  const [isSorting, setIsSorting] = useState(false);
  const { handleDragOver, stopAutoScroll } = useDragAutoScroll();

  const handleToggleSorting = () => {
    if (isSorting) onReorderCommit();
    setIsSorting((sorting) => !sorting);
  };

  // A session can also end without the Done button — switching to the table view, leaving the page,
  // or applying a filter (which hides the button). Each of those must still save the arrangement.
  const commitRef = useRef(onReorderCommit);
  const isSortingRef = useRef(isSorting);
  useLayoutEffect(() => {
    commitRef.current = onReorderCommit;
    isSortingRef.current = isSorting;
  });
  useEffect(() => () => void (isSortingRef.current && commitRef.current()), []);

  useEffect(() => {
    if (!isSorting || isReorderEnabled) return;
    setIsSorting(false);
    commitRef.current();
  }, [isSorting, isReorderEnabled]);

  return (
    <div className="tw-flex tw-h-full tw-gap-3 tw-pb-2">
      {STATUS_ORDER.map((status) => {
        const isCompleted = status === InfluencerCampaignStatus.Completed;
        const collapsed = isCompleted && completedCollapsed;
        const items = grouped[status];
        const meta = STATUS_META[status];
        // Only In progress feeds the creator Discover order, and reordering a filtered column would
        // reshuffle the campaigns hidden from it — so the controls stay off unless the column is whole.
        const isColumnReorderable =
          isReorderEnabled && status === InfluencerCampaignStatus.InProgress;
        const isColumnSorting = isColumnReorderable && isSorting;
        return (
          <div
            key={status}
            className={cn(
              "tw-flex tw-flex-col tw-gap-2 tw-rounded-xl tw-bg-muted/40 tw-p-3 tw-transition-[flex-basis,padding,background-color] tw-duration-200",
              collapsed
                ? "tw-basis-[52px] tw-flex-shrink-0 tw-cursor-pointer tw-px-1.5"
                : "tw-min-w-0 tw-flex-1",
            )}
            onClick={collapsed ? onToggleCompleted : undefined}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              e.currentTarget.classList.add("tw-ring-2", "tw-ring-primary/30");
            }}
            onDragLeave={(e) => {
              e.currentTarget.classList.remove("tw-ring-2", "tw-ring-primary/30");
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("tw-ring-2", "tw-ring-primary/30");
              const campaignId = e.dataTransfer.getData("text/campaignId");
              if (campaignId) onCardDrop(campaignId, status);
            }}
          >
            <div
              className={cn(
                "tw-flex tw-items-center tw-justify-between",
                collapsed && "tw-flex-col tw-gap-2",
              )}
            >
              <div
                className={cn(
                  "tw-flex tw-items-center tw-gap-1.5",
                  collapsed && "tw-flex-col tw-gap-2",
                )}
              >
                {isCompleted && (
                  <button
                    type="button"
                    aria-label="Toggle Completed"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleCompleted();
                    }}
                    className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-text-muted-foreground hover:tw-bg-accent"
                  >
                    {collapsed ? (
                      <ChevronLeft className="tw-h-3 tw-w-3" />
                    ) : (
                      <ChevronRight className="tw-h-3 tw-w-3" />
                    )}
                  </button>
                )}
                <span
                  className={cn(
                    "tw-text-xs tw-font-semibold",
                    meta.tone,
                    collapsed &&
                      "tw-[writing-mode:vertical-rl] tw-[transform:rotate(180deg)]",
                  )}
                >
                  {meta.label}
                </span>
                <Badge
                  variant="secondary"
                  className={cn("tw-h-5 tw-px-1.5 tw-text-[10px]", meta.pill)}
                >
                  {items.length}
                </Badge>
              </div>
              {isColumnReorderable && !collapsed && (
                <button
                  type="button"
                  aria-label={isSorting ? "Done reordering" : "Reorder campaigns"}
                  aria-pressed={isSorting}
                  onClick={handleToggleSorting}
                  className={cn(
                    "tw-flex tw-items-center tw-gap-1 tw-rounded-md tw-border tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-medium tw-transition-colors",
                    isSorting
                      ? "tw-border-primary tw-bg-primary tw-text-primary-foreground"
                      : "tw-border-border tw-bg-background tw-text-muted-foreground hover:tw-bg-accent hover:tw-text-foreground",
                  )}
                >
                  <ArrowUpDown className="tw-h-3 tw-w-3" />
                  {isSorting ? "Done" : "Reorder"}
                </button>
              )}
            </div>

            {!collapsed && (
              <div
                className="tw-flex tw-flex-col tw-gap-2 tw-overflow-y-auto"
                onDragOver={isColumnSorting ? handleDragOver : undefined}
                onDragEnd={isColumnSorting ? stopAutoScroll : undefined}
                onDrop={isColumnSorting ? stopAutoScroll : undefined}
              >
                {items.map((campaign, index) => (
                  <CampaignCard
                    key={campaign.campaignId}
                    campaign={campaign}
                    onClick={() => onCardClick(campaign)}
                    onEdit={() => onCardEdit(campaign)}
                    onMoveUp={
                      isColumnSorting && index > 0
                        ? () =>
                            onReorder(
                              moveCampaignInOrder(items, {
                                fromIndex: index,
                                toIndex: index - 1,
                              }),
                            )
                        : undefined
                    }
                    onMoveDown={
                      isColumnSorting && index < items.length - 1
                        ? () =>
                            onReorder(
                              moveCampaignInOrder(items, {
                                fromIndex: index,
                                toIndex: index + 1,
                              }),
                            )
                        : undefined
                    }
                    onReorderDrop={
                      isColumnSorting
                        ? (draggedCampaignId) => {
                            const fromIndex = items.findIndex(
                              (candidate) =>
                                candidate.campaignId === draggedCampaignId,
                            );
                            if (fromIndex < 0 || fromIndex === index) return;
                            onReorder(
                              moveCampaignInOrder(items, {
                                fromIndex,
                                toIndex: index,
                              }),
                            );
                          }
                        : undefined
                    }
                    isCompact={isColumnSorting}
                  />
                ))}
                {items.length === 0 && (
                  <div className="tw-rounded-md tw-border tw-border-dashed tw-border-border tw-px-3 tw-py-4 tw-text-center tw-text-xs tw-text-muted-foreground">
                    No campaigns
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
